import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Question, TestResult as TestResultType } from '../../types/test';
import { TestTimer } from './TestTimer';
import { TestResult } from './TestResult';
import { FileText, X } from 'lucide-react';
import { QuestionHtmlContent } from './QuestionHtmlContent';
import { serverNow } from '../../utils/serverTime';

export interface TestQuestionProps {
  question: Question;
  questionNum?: number | undefined; // 実際の問題番号（question.sort_keyと同じ）
  displayNumber?: number | null | undefined; // 表示用の通し番号（format_type=questionの場合のみ）
  selectedChoiceId?: number | null | undefined;
  isSubmitted: boolean;
  isAnswerSent?: boolean;
  /** 回答送信リトライ中（サーバー受理待ち）。選択肢をロックし「回答情報送信中…」を表示する */
  isSending?: boolean;
  /** 回答送信がリトライ上限に到達して失敗した。エラーメッセージを表示する */
  isSendFailed?: boolean;
  onSelectChoice: (choiceId: number) => void;
  onSubmit: () => void;
  onTimeout?: () => void;
  isArchive?: boolean;
  archiveSelectedChoiceId?: number | undefined;
  results?: TestResultType[];
  onTimerTick?: (remainingTime: number) => void;
  /** 回答締切時刻（epoch ms、サーバー時刻基準）。TestTimer が serverNow() との差分で残り時間を計算する */
  deadlineMs?: number | undefined;
  remainingTime?: number | undefined;
  isExplaining?: boolean; // talliedステータス（集計完了=解説中）の場合にtrue
  isTrialUser?: boolean; // 試用ユーザーの場合にtrue
  lectureId?: string | undefined; // ローカルストレージから回答を取得するために必要
  userId?: string | undefined; // ローカルストレージから回答を取得するために必要
}

/**
 * DifficultyIndicator - 難易度と★のみを表示
 */
function DifficultyIndicator({ level }: { level: number }) {
  const stars = level; // levelがそのまま星の数

  return (
    <div className="difficulty-stars" aria-label={`難易度: ${stars}つ星`}>
      <span className="difficulty-label">難易度</span>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={`star ${i < stars ? 'filled' : ''}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

const isPdfUrl = (url: string): boolean => url.toLowerCase().endsWith('.pdf');

/** ブラウザ内蔵PDFビューアのツールバー（DL/印刷等）を非表示にする */
const getPdfEmbedUrl = (url: string): string => {
  const params = 'toolbar=0&navpanes=0';
  return url.includes('#') ? `${url}&${params}` : `${url}#${params}`;
};

type QuestionImageItem = { url: string; label?: string };

/**
 * QuestionImages - 管理画面と同様にサムネイルを横並び表示し、クリックで拡大する。
 */
export function QuestionImages({ images }: { images: Array<QuestionImageItem> }) {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [selectedImage, setSelectedImage] = useState<QuestionImageItem | null>(null);

  const handleImageError = useCallback((src: string) => {
    setFailedImages((prev) => new Set([...prev, src]));
  }, []);

  const validImages = images.filter(
    (img) =>
      img &&
      img.url &&
      typeof img.url === 'string' &&
      img.url.trim() !== '' &&
      !failedImages.has(img.url),
  );

  const rasterImages = validImages.filter((img) => !isPdfUrl(img.url));
  const pdfImages = validImages.filter((img) => isPdfUrl(img.url));

  const closePreview = useCallback(() => {
    setSelectedImage(null);
  }, []);

  useEffect(() => {
    if (!selectedImage) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreview();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, closePreview]);

  if (validImages.length === 0) {
    return null;
  }

  const selectedIsPdf = selectedImage ? isPdfUrl(selectedImage.url) : false;

  const previewModal = selectedImage
    ? createPortal(
        <div
          className={`test-image-modal ${selectedIsPdf ? 'is-pdf' : 'is-image'}`}
          role="dialog"
          aria-modal="true"
          aria-label="問題画像の拡大表示"
          onClick={closePreview}
        >
          <button
            type="button"
            className="test-image-modal-close-float"
            onClick={closePreview}
            aria-label="拡大表示を閉じる"
          >
            <X aria-hidden="true" />
          </button>

          <div
            className="test-image-modal-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="test-image-modal-header">
              <div className="test-image-modal-title">
                <p className="test-image-modal-name">
                  {selectedImage.label || (selectedIsPdf ? 'PDF' : '問題画像')}
                </p>
              </div>
              <button
                type="button"
                className="test-image-modal-close"
                onClick={closePreview}
              >
                <X aria-hidden="true" />
                閉じる
              </button>
            </div>

            {selectedIsPdf ? (
              <iframe
                src={getPdfEmbedUrl(selectedImage.url)}
                title={selectedImage.label || 'PDF'}
                className="test-image-modal-pdf"
              />
            ) : (
              <div className="test-image-modal-body">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.label || '問題画像'}
                  className="test-image-modal-img"
                />
                {selectedImage.label && (
                  <p className="test-image-modal-caption">{selectedImage.label}</p>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <div className="test-image-section" aria-label="問題画像・資料">
        {rasterImages.length > 0 && (
          <>
            <div className="test-image-section-header">
              <span className="test-image-badge">問題画像</span>
              <span className="test-image-count">({rasterImages.length}枚)</span>
            </div>

            <div className="test-image-grid-large">
              {rasterImages.map((image, index) => {
                const cardLabel = image.label || `図${index + 1}`;

                return (
                  <button
                    key={image.url}
                    type="button"
                    className="test-image-card test-image-card-large"
                    onClick={() => setSelectedImage(image)}
                    aria-label={`${cardLabel}を拡大表示`}
                  >
                    <div className="test-image-card-preview test-image-card-preview-large">
                      <img
                        src={image.url}
                        alt={cardLabel}
                        onError={() => handleImageError(image.url)}
                        loading="lazy"
                      />
                    </div>
                    <span className="test-image-card-label">{cardLabel}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {pdfImages.length > 0 && (
          <>
            <div
              className={`test-image-section-header${rasterImages.length > 0 ? ' test-image-section-header-materials' : ''}`}
            >
              <span className="test-image-badge">資料</span>
              <span className="test-image-count">({pdfImages.length}件)</span>
            </div>

            <div className="test-image-grid-compact test-image-grid-materials">
              {pdfImages.map((image, index) => {
                const cardLabel = image.label || `資料${index + 1}`;

                return (
                  <button
                    key={image.url}
                    type="button"
                    className="test-image-card test-image-card-compact test-image-card-material"
                    onClick={() => setSelectedImage(image)}
                    aria-label={`${cardLabel}を表示`}
                  >
                    <div className="test-image-card-preview test-image-card-preview-compact test-image-card-preview-material">
                      <span className="test-image-card-pdf">
                        <FileText aria-hidden="true" />
                        <span>PDF</span>
                      </span>
                    </div>
                    <span className="test-image-card-label test-image-card-label-material">{cardLabel}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {previewModal}
    </>
  );
}

/**
 * TestQuestion - Main component for displaying and answering test questions.
 *
 * Features:
 * - Question text and optional images display
 * - Choice selection with visual feedback
 * - Countdown timer integration
 * - Difficulty level indicator
 * - One-click answer submission (選択肢クリックで即時送信。「回答する」ボタンは廃止)
 * - Result display after submission
 * - Archive mode with past answer display
 *
 * Validates: Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.8, 6.9, 6.10
 */
export function TestQuestion({
  question,
  questionNum: _questionNum, // 未使用だがAPIとの互換性のため保持
  displayNumber,
  selectedChoiceId,
  isSubmitted,
  isAnswerSent = false,
  isSending = false,
  isSendFailed = false,
  onSelectChoice,
  onSubmit,
  onTimeout,
  isArchive = false,
  archiveSelectedChoiceId,
  results = [],
  onTimerTick,
  deadlineMs,
  remainingTime,
  userId,
  isExplaining = false,
  isTrialUser = false,
  lectureId,
}: TestQuestionProps) {
  const [timerActive, setTimerActive] = useState(false);
  const timerStartedRef = useRef<number | null>(null); // 開始した問題IDを記録
  // 制限時間到達フラグ。サーバーの締切処理（status が tallying へ変わる）より先に
  // クライアント側で選択肢をロックし、制限時間後の回答（選択＝送信）を防ぐ
  const [isTimeUp, setIsTimeUp] = useState(false);

  // 同じ問題がSP用/PC用レイアウトで同時にDOMに存在するため、
  // ラジオグループ名をインスタンスごとに一意にする
  // （name重複だとブラウザが別インスタンス側のチェックを外してしまい、
  //   1回のクリックで選択状態にならない不具合が発生する）
  const radioGroupId = useId();

  // Sort choices by sort_key
  const sortedChoices = useMemo(
    () => [...question.choices].sort((a, b) => a.sort_key - b.sort_key),
    [question.choices],
  );

  // Start timer when question status becomes 'answering'
  // answeringステータスになったときにタイマーを開始する
  // タイマーは制限時間まで継続（回答送信後も停止しない）
  useEffect(() => {
    if (isSubmitted || isArchive) {
      setTimerActive(false);
      timerStartedRef.current = null;
      return;
    }

    // answeringステータスになったときにタイマーを開始
    const isAnswering = question.status === 'answering';
    
    // 問題IDが変わった、またはまだ開始していない場合にタイマーを開始
    if (question.time_limit > 0 && isAnswering && timerStartedRef.current !== question.id) {
      timerStartedRef.current = question.id;
      setTimerActive(true);
      // 新しい出題（テスト中止→再出題を含む）ではタイムアップ状態を解除する
      setIsTimeUp(false);
    } else if (!isAnswering) {
      // answeringステータスでない場合はタイマーを停止
      setTimerActive(false);
      if (timerStartedRef.current === question.id) {
        timerStartedRef.current = null;
      }
    }
  }, [isSubmitted, isArchive, question.time_limit, question.id, question.status, timerActive]);

  // タイムアウト時はタイマーを停止し、選択肢をロックする（回答は送信しない。
  // 回答はユーザーが選択肢をクリックした時点で送信済みのため）
  const handleTimeout = useCallback(() => {
    setTimerActive(false);
    setIsTimeUp(true);
    onTimeout?.();
  }, [onTimeout]);

  // 選択肢のクリックで選択と同時に回答を送信する（1クリック回答）
  // 送信中（isSending）は選択をロックし、画面表示とサーバー記録のズレ・二重送信を防ぐ
  // 制限時間到達後（isTimeUp）は選択をロックし、締切後の回答を防ぐ
  // 制限時間内であれば別の選択肢をクリックして回答を変更できる
  const handleSelectChoice = useCallback(
    (choiceId: number) => {
      if (isSubmitted || isArchive || isSending || isTimeUp) {
        return;
      }
      // タブ非アクティブ時のタイマー遅延等でタイムアウト未発火のまま締切時刻を
      // 過ぎているケースの防御（サーバー時刻基準で判定）
      if (deadlineMs !== undefined && serverNow() >= deadlineMs) {
        setIsTimeUp(true);
        return;
      }
      onSelectChoice(choiceId);
      // タイマーは停止しない（制限時間まで継続、再回答可能）
      onSubmit();
    },
    [isSubmitted, isArchive, isSending, isTimeUp, deadlineMs, onSelectChoice, onSubmit],
  );

  // 矢印キーによるラジオボタンのフォーカス移動（＝選択変更）を無効化する。
  // 選択と同時に回答が送信されるため、矢印キー操作が意図しない回答送信になるのを防ぐ
  // （Tab でのフォーカス移動と Space での明示的な選択は従来どおり可能）
  const handleChoiceKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      event.preventDefault();
    }
  }, []);

  // Determine which choice ID to display (archive mode shows past answer)
  const displayedChoiceId = isArchive
    ? archiveSelectedChoiceId
    : selectedChoiceId;

  // タイマーは制限時間まで表示（回答後も継続表示）
  const showTimer = !isSubmitted && !isArchive && !isExplaining && question.time_limit > 0;
  const isAnswering = question.status === 'answering';
  const showTrialNotice = isTrialUser && (isAnswering || isExplaining);

  // format_type に応じた表示ラベルとヘッダーテキストを生成
  const getQuestionLabel = () => {
    if (question.format_type === 'survey') {
      return 'アンケート';
    } else if (question.format_type === 'example') {
      return '例題';
    } else {
      return '問題';
    }
  };

  const getQuestionHeaderText = () => {
    const label = getQuestionLabel();
    // questionの場合はdisplayNumberを使用、それ以外はquestionNumまたはsort_keyを使用
    if (question.format_type === 'question' && displayNumber != null) {
      const text = `${label}${displayNumber}`;
      return text;
    } else {
      // 例題・アンケートの場合は通し番号を表示しない
      return label;
    }
  };

  return (
    <div className="test-card">
      {/* Header */}
      <div className="test-header-top">
        <div className="test-header-left">
          <h4>{getQuestionHeaderText()}</h4>
          {/* Difficulty indicator */}
          <DifficultyIndicator level={question.level} />
        </div>

        {/* Timer */}
        {showTimer && (
          <TestTimer
            timeLimit={question.time_limit}
            isActive={timerActive}
            onTimeout={handleTimeout}
            {...(deadlineMs !== undefined && { deadlineMs })}
            {...(remainingTime !== undefined && { remainingTime })}
            {...(onTimerTick !== undefined && { onTick: onTimerTick })}
          />
        )}
      </div>

      {/* 試用ユーザー通知 */}
      {showTrialNotice && (
        <div className="test-trial-notice" role="alert">
          <div className="test-trial-notice-icon" aria-hidden="true">⚠</div>
          <div className="test-trial-notice-text">
            試用ユーザーの方は、解答の記録・集計はされません。
          </div>
        </div>
      )}

      {/* Question content */}
      {isSubmitted || isArchive || isExplaining ? (
        // 結果・解説表示時: ステータス→KPI→グラフ→問題文→画像→解法→選択肢 の順
        <div className="test-result-problem-area">
          {/* ステータス + あなたの解答/正解/正答率 + 回答分布 */}
          <TestResult
            choices={question.choices}
            selectedChoiceId={displayedChoiceId ?? null}
            chartStyle={question.chart_style}
            results={results}
            isExplaining={isExplaining}
            formatType={question.format_type}
            lectureId={lectureId}
            userId={userId}
            questionId={question.id}
            part="summary"
            questionStatus={question.status}
          />

          {/* 問題文 */}
          <QuestionHtmlContent html={question.question_text} className="test-question test-html-content" />

          {/* 問題画像・資料 */}
          {question.question_images && question.question_images.length > 0 && (
            <QuestionImages images={question.question_images} />
          )}

          {/* 解法のポイント */}
          {question.help_text && (
            <div className="solution-point-card-inline">
              解法のポイント:{' '}
              <QuestionHtmlContent
                html={question.help_text}
                className="test-html-content test-html-inline"
                as="span"
              />
            </div>
          )}

          {/* 選択肢 */}
          <TestResult
            choices={question.choices}
            selectedChoiceId={displayedChoiceId ?? null}
            chartStyle={question.chart_style}
            results={results}
            isExplaining={isExplaining}
            formatType={question.format_type}
            lectureId={lectureId}
            userId={userId}
            questionId={question.id}
            part="choices"
            questionStatus={question.status}
          />
        </div>
      ) : (
        // 回答中: 問題文→画像→解法→選択肢 の順
        <div className="test-result-problem-area">
          {/* Question text */}
          <QuestionHtmlContent html={question.question_text} className="test-question test-html-content" />

          {/* Question images / materials */}
          {question.question_images && question.question_images.length > 0 && (
            <QuestionImages images={question.question_images} />
          )}

          {/* Help text（解法のポイント） */}
          {question.help_text && (
            <div className="solution-point-card-inline">
              解法のポイント:{' '}
              <QuestionHtmlContent
                html={question.help_text}
                className="test-html-content test-html-inline"
                as="span"
              />
            </div>
          )}

          <div
            className="test-options"
            role="radiogroup"
            aria-label="選択肢"
            onKeyDown={handleChoiceKeyDown}
          >
            {sortedChoices.map((choice, index) => (
              <label
                key={choice.id}
                className={`test-option ${selectedChoiceId === choice.id ? 'selected' : ''} ${isTimeUp ? 'time-up' : ''}`}
              >
                {/* ラジオボタンは視覚的に非表示（キーボード操作・スクリーンリーダー対応のため DOM には残す） */}
                <input
                  type="radio"
                  className="test-option-radio-hidden"
                  name={`question-${question.id}-${radioGroupId}`}
                  value={choice.id}
                  checked={selectedChoiceId === choice.id}
                  onChange={() => handleSelectChoice(choice.id)}
                  disabled={isSubmitted || isTimeUp}
                />
                {/* ラジオボタンの位置に選択肢番号を表示（結果画面と同じ「1.」形式） */}
                <span className="test-option-index" aria-hidden="true">{index + 1}.</span>
                <QuestionHtmlContent html={choice.choice} className="test-html-content" />
              </label>
            ))}
          </div>
          {/* 回答送信ステータス表示（選択肢クリックで即時送信するため送信ボタンはない） */}
          <div className="test-submit-area">
            {isSending ? (
              <div className="test-submit-hint test-submit-hint-sending" role="status">
                解答情報送信中…
              </div>
            ) : isTimeUp ? (
              <div className="test-submit-hint" role="status">
                解答を締め切りました
              </div>
            ) : isSendFailed ? (
              <div className="test-submit-hint test-submit-hint-error" role="alert">
                解答送信時にエラーが発生しました。お手数ですが再度解答を選択してください
              </div>
            ) : isAnswerSent ? (
              <div className="test-submit-hint">
                制限時間内であれば解答を変更できます
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
